import { useCallback, useState } from "react";

import { useCreateClientRunProgram } from "@/hooks/useClientPrograms";
import { fetchClientEffectivePolicies } from "@/services/clientPrograms";
import { updateProgramPolicyValue } from "@/services/programPolicies";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import type { BuilderConfig } from "../Programs";

function boundedNumber(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

function dateAfterDays(days: number): string {
    return new Date(Date.now() + Math.max(1, days) * 86_400_000).toISOString().slice(0, 10);
}

function requiredPolicyParametersForConfig(config: BuilderConfig): Record<string, Record<string, unknown>> {
    const today = new Date().toISOString().slice(0, 10);
    return {
        "program-duration":
            config.programType === "fixed"
                ? {
                      mode: "fixed",
                      startAt: today,
                      endAt: dateAfterDays(config.periodDays)
                  }
                : {
                      mode: "continuous",
                      startAt: "immediate",
                      endAt: null
                  },
        "rewards-model": {
            rewardsPct: boundedNumber(config.rewardsPct, 0, 100),
            baseApyBps: boundedNumber(Math.round(config.baseApy * 100), 0, 10000)
        },
        "settlement-cycle": {
            length: Math.max(1, Math.round(config.settlementCycleLength)),
            unit: config.settlementCycleUnit
        },
        // The `client-added-funding-sources` required policy is sent hardcoded
        // to allowed. Funding is no longer a stored choice — it's DERIVED from
        // the Budget Type and shown as a read-only Configuration field — so
        // there is nothing funding-related to sync here; this required-policy
        // contract stays intact. See the note in
        // `useDraftProgram.toBuilderConfig`.
        "client-added-funding-sources": {
            allowed: true
        }
    };
}

async function syncClientRequiredProgramConfiguration(programUuid: string, config: BuilderConfig): Promise<void> {
    const response = await fetchClientEffectivePolicies(programUuid);
    const parametersByRuleType = requiredPolicyParametersForConfig(config);
    await Promise.all(
        response.data
            .filter((policy) => policy.ruleType in parametersByRuleType)
            .filter((policy) => policy.readOnly !== true)
            .map((policy) =>
                updateProgramPolicyValue({
                    policyUuid: policy.uuid,
                    parameters: parametersByRuleType[policy.ruleType]!,
                    scopeType: "Program",
                    scopeTargetUuids: [programUuid]
                })
            )
    );
}

/**
 * Dependencies the activation orchestration needs from the Programs page —
 * the fork input (parent-program lookup) and the rail-reconciliation callbacks
 * (draft removal, selection, tab nudge, mock commit). Keeping these as props
 * means the hook owns the activation state + mutations + async orchestration
 * while the page keeps ownership of the rail state it reconciles.
 */
export interface UseActivateClientProgramOptions {
    /** Parent-program uuid for a draft id, or `undefined` for mock drafts. */
    parentProgramUuidFor: (programId: string) => string | undefined;
    /** Mock path: commit the activated program into the local rail state. */
    commitActivatedProgram: (programId: string, config: BuilderConfig) => void;
    /** Live path: remove the draft row once its live program is created. */
    onDraftRemoved: (programId: string) => void;
    /** Live path: select the created live program + nudge the visible tab. */
    onProgramCreated: (createdProgramUuid: string) => void;
}

export interface UseActivateClientProgram {
    activate: (programId: string, config: BuilderConfig) => void;
    isActivating: (programId: string) => boolean;
    activationError: (programId: string) => string | undefined;
    /**
     * The set of draft ids whose live-create is in flight. The page needs
     * this to reconcile the rail (draft-merge + selection) while a create is
     * pending — it's the same `pendingCreateDraftIds` the page used to own.
     */
    pendingCreateDraftIds: ReadonlySet<string>;
}

export function useActivateClientProgram(options: UseActivateClientProgramOptions): UseActivateClientProgram {
    const { parentProgramUuidFor, commitActivatedProgram, onDraftRemoved, onProgramCreated } = options;

    const createClientRunProgram = useCreateClientRunProgram();

    const [pendingCreateDraftIds, setPendingCreateDraftIds] = useState<ReadonlySet<string>>(() => new Set());
    const [activationErrors, setActivationErrors] = useState<Record<string, string>>({});

    const activate = useCallback(
        (programId: string, config: BuilderConfig): void => {
            const parentProgramUuid = parentProgramUuidFor(programId);
            setActivationErrors((prev) => {
                const next = { ...prev };
                delete next[programId];
                return next;
            });
            if (parentProgramUuid) {
                setPendingCreateDraftIds((prev) => {
                    const next = new Set(prev);
                    next.add(programId);
                    return next;
                });
                void (async (): Promise<void> => {
                    try {
                        const createdProgram = await createClientRunProgram.mutateAsync({
                            parentProgramUuid,
                            name: config.name.trim() || "Company Recipient Program",
                            description: "Company Recipient-run incentive program",
                            status: "Active"
                        });
                        let followUpError: string | null = null;
                        try {
                            await syncClientRequiredProgramConfiguration(createdProgram.uuid, config);
                        } catch (error) {
                            followUpError = resolveClientFormError(
                                error,
                                "policy",
                                "Program was created, but required policy settings were not saved. Open Policies and review distributor constraints."
                            );
                        }
                        // Funding is derived from the Budget Type (a read-only
                        // Configuration field), not a set of attachable
                        // funding-source accounts, so there is nothing to attach
                        // here and no backend sync path (see the note in
                        // `useDraftProgram.toBuilderConfig`).
                        onDraftRemoved(programId);
                        onProgramCreated(createdProgram.uuid);
                        setActivationErrors((prev) => {
                            const next = { ...prev };
                            delete next[programId];
                            if (followUpError) next[createdProgram.uuid] = followUpError;
                            return next;
                        });
                    } catch (error) {
                        setActivationErrors((prev) => ({
                            ...prev,
                            [programId]: resolveClientFormError(
                                error,
                                "program",
                                "Program was not created. Check the parent Program and try again."
                            )
                        }));
                    } finally {
                        setPendingCreateDraftIds((prev) => {
                            const next = new Set(prev);
                            next.delete(programId);
                            return next;
                        });
                    }
                })();
                return;
            }

            commitActivatedProgram(programId, config);
        },
        [parentProgramUuidFor, commitActivatedProgram, onDraftRemoved, onProgramCreated, createClientRunProgram]
    );

    const isActivating = useCallback(
        (programId: string): boolean => pendingCreateDraftIds.has(programId),
        [pendingCreateDraftIds]
    );

    const activationError = useCallback(
        (programId: string): string | undefined => activationErrors[programId],
        [activationErrors]
    );

    return { activate, isActivating, activationError, pendingCreateDraftIds };
}
